import { useState, useEffect, useCallback } from 'react'
import {
  Search, Calendar, User, Package, Euro,
  CheckCircle, XCircle, Truck, Clock, AlertCircle, Eye,
  RefreshCw, TestTube, Banknote, CreditCard, Building, Gift, Filter, ArrowLeftRight, BanIcon,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X
} from 'lucide-react'
import api from '@/lib/api'
import { timeAgo, hoursElapsed } from '@/lib/utils'
import ReturnDetailModal from './ReturnDetailModal'
import ApproveReturnModal from './ApproveReturnModal'
import RejectReturnModal from './RejectReturnModal'
import ReceiveReturnModal from './ReceiveReturnModal'
import TestReturnModal from './TestReturnModal'
import RefundReturnModal from './RefundReturnModal'
import ReplacementReturnModal from './ReplacementReturnModal'
import RejectRefundModal from './RejectRefundModal'
import WarrantyResultModal from '../incidents/WarrantyResultModal'

interface Return {
  id: number
  code: string
  order_id: number
  order_reference: string
  order_date: string
  customer_id: number
  customer_email: string
  customer_name: string
  customer_nif: string | null
  customer_type: string
  status: string
  reason: string
  product_amount: number
  shipping_cost: number
  refund_amount: number
  carrier_code: string | null
  pickup_tracking: string | null
  test_result: string
  created_at: string
  rejection_reason: string | null
  payment_method: string | null
  refund_method: string | null
  refund_iban: string | null
  refund_iban_holder: string | null
  is_prestashop_customer: boolean
  case_type: string
  albaran_number: string | null
  albaran_client: number | null
  carrier_claim_reference: string | null
  carrier_claim_status: string | null
  original_invoice_number: string | null
  incident_carrier: string | null
  incident_subtype: string | null
  incident_subtype_other: string | null
  incident_tracking: string | null
  is_canje: boolean
  pickup_address?: {
    street?: string
    city?: string
    postal_code?: string
    country_code?: string
    phone?: string
  } | null
}

interface ReturnRateInfo {
  rate: number
  total_invoices: number
  total_returns: number
  codcli: number
}

interface Props {
  statusFilter?: string
  showRefundMethodChips?: boolean
  onStatsChange?: () => void
  caseType?: string
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  requested: { label: 'Solicitada', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Clock },
  approved: { label: 'Aprobada', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: CheckCircle },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
  pickup_scheduled: { label: 'Recogida prog.', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300', icon: Truck },
  in_transit: { label: 'En transito', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', icon: Truck },
  received: { label: 'Recibida', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300', icon: Package },
  tested: { label: 'Testeada', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300', icon: CheckCircle },
  refunded: { label: 'Reembolsada', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: Euro },
  replacement: { label: 'Cambio', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', icon: RefreshCw },
  refund_rejected: { label: 'Reembolso rechazado', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300', icon: XCircle },
  cancelled: { label: 'Cancelada', color: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300', icon: XCircle },
  warranty_approved: { label: 'Garantia aprobada', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle },
  warranty_rejected: { label: 'Garantia rechazada', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
  warranty_returned: { label: 'Devuelto al cliente', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: Truck },
  closed: { label: 'Cerrada', color: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300', icon: CheckCircle }
}

const CUSTOMER_TYPE_COLORS: Record<string, string> = {
  PVP: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  PVD: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  VIP: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
}

const REFUND_METHOD_CONFIG: Record<string, { label: string; shortLabel: string; color: string; icon: React.ElementType }> = {
  redsys: { label: 'Tarjeta', shortLabel: 'Tarjeta', color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30', icon: CreditCard },
  transfer: { label: 'Transferencia', shortLabel: 'Transfer.', color: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/30', icon: Building },
  store_credit: { label: 'Cupon', shortLabel: 'Cupon', color: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/30', icon: Gift }
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-4 py-3.5"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-28" /><div className="h-3 bg-slate-100 dark:bg-slate-700/60 rounded w-20 mt-1.5" /></td>
      <td className="px-4 py-3.5"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32" /><div className="h-3 bg-slate-100 dark:bg-slate-700/60 rounded w-24 mt-1.5" /></td>
      <td className="px-4 py-3.5 text-center"><div className="h-5 bg-slate-200 dark:bg-slate-700 rounded-full w-12 mx-auto" /></td>
      <td className="px-4 py-3.5"><div className="h-5 bg-slate-200 dark:bg-slate-700 rounded-full w-20" /></td>
      <td className="px-4 py-3.5"><div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-16" /></td>
      <td className="px-4 py-3.5 text-right"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16 ml-auto" /></td>
      <td className="px-4 py-3.5"><div className="flex gap-1.5 justify-center"><div className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-7" /><div className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-20" /></div></td>
    </tr>
  )
}

export default function ReturnsListTab({ statusFilter, showRefundMethodChips, onStatsChange, caseType }: Props) {
  const isIncidents = caseType !== undefined && caseType !== 'return'
  const [returns, setReturns] = useState<Return[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [customerTypeFilter, setCustomerTypeFilter] = useState('')
  const [refundMethodFilter, setRefundMethodFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const limit = 50

  // Return rates
  const [returnRates, setReturnRates] = useState<Record<string, ReturnRateInfo>>({})
  const [loadingRates, setLoadingRates] = useState(false)

  // Modales
  const [selectedReturn, setSelectedReturn] = useState<Return | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [receiveModalOpen, setReceiveModalOpen] = useState(false)
  const [testModalOpen, setTestModalOpen] = useState(false)
  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const [replacementModalOpen, setReplacementModalOpen] = useState(false)
  const [rejectRefundModalOpen, setRejectRefundModalOpen] = useState(false)
  const [warrantyResultModalOpen, setWarrantyResultModalOpen] = useState(false)
  const [warrantyDefaultApproved, setWarrantyDefaultApproved] = useState<boolean | null>(null)

  const getReturnRateKey = (ret: Return): string => {
    const nif = (ret.customer_nif || '').toUpperCase().replace(/[\s\-.]/g, '')
    if (nif.length >= 5) return nif
    return (ret.customer_email || '').trim().toLowerCase()
  }

  const loadReturnRates = useCallback(async (returnsList: Return[]) => {
    const seen = new Set<string>()
    const customers: { nif?: string; email?: string }[] = []

    for (const r of returnsList) {
      const key = getReturnRateKey(r)
      if (!key || seen.has(key) || key in returnRates) continue
      seen.add(key)
      customers.push({
        nif: r.customer_nif || undefined,
        email: r.customer_email || undefined
      })
    }

    if (customers.length === 0) return

    setLoadingRates(true)
    try {
      const response = await api.post('/returns/admin/return-rates', { customers })
      if (response.data.rates) {
        setReturnRates(prev => ({ ...prev, ...response.data.rates }))
      }
    } catch (error) {
      console.error('Error loading return rates:', error)
    } finally {
      setLoadingRates(false)
    }
  }, [returnRates])

  const loadReturns = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (refundMethodFilter) params.append('refund_method', refundMethodFilter)
      if (customerTypeFilter) params.append('customer_type', customerTypeFilter)
      if (search) params.append('search', search)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)
      if (caseType) params.append('case_type', caseType)
      params.append('limit', String(limit))
      params.append('offset', String(page * limit))

      const response = await api.get(`/returns/admin?${params.toString()}`)
      const returnsList = response.data.returns || []
      setReturns(returnsList)
      setTotal(response.data.total || 0)
      loadReturnRates(returnsList)
    } catch (error) {
      console.error('Error loading returns:', error)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, refundMethodFilter, customerTypeFilter, search, dateFrom, dateTo, page, caseType])

  useEffect(() => {
    loadReturns()
  }, [loadReturns])

  const handleActionComplete = () => {
    loadReturns()
    onStatsChange?.()
    setSelectedReturn(null)
  }

  const openActionModal = (ret: Return, action: string) => {
    setSelectedReturn(ret)
    switch (action) {
      case 'detail': setDetailModalOpen(true); break
      case 'approve': setApproveModalOpen(true); break
      case 'reject': setRejectModalOpen(true); break
      case 'receive': setReceiveModalOpen(true); break
      case 'test': setTestModalOpen(true); break
      case 'refund': setRefundModalOpen(true); break
      case 'replacement': setReplacementModalOpen(true); break
      case 'reject_refund': setRejectRefundModalOpen(true); break
      case 'warranty_approve': setWarrantyDefaultApproved(true); setWarrantyResultModalOpen(true); break
      case 'warranty_reject': setWarrantyDefaultApproved(false); setWarrantyResultModalOpen(true); break
    }
  }

  const getAvailableActions = (ret: Return) => {
    const actions: { label: string; action: string; bgColor: string; icon: React.ElementType }[] = []
    switch (ret.status) {
      case 'requested':
        actions.push({ label: 'Aprobar', action: 'approve', bgColor: 'bg-green-500 hover:bg-green-600 text-white', icon: CheckCircle })
        actions.push({ label: 'Rechazar', action: 'reject', bgColor: 'bg-red-500 hover:bg-red-600 text-white', icon: XCircle })
        break
      case 'approved':
      case 'pickup_scheduled':
      case 'in_transit':
        actions.push({ label: 'Recibir', action: 'receive', bgColor: 'bg-indigo-500 hover:bg-indigo-600 text-white', icon: Package })
        break
      case 'received':
        actions.push({ label: 'Testar', action: 'test', bgColor: 'bg-violet-500 hover:bg-violet-600 text-white', icon: TestTube })
        break
      case 'tested':
        if (ret.case_type === 'warranty') {
          actions.push({ label: 'Procedente', action: 'warranty_approve', bgColor: 'bg-green-500 hover:bg-green-600 text-white', icon: CheckCircle })
          actions.push({ label: 'No procedente', action: 'warranty_reject', bgColor: 'bg-red-500 hover:bg-red-600 text-white', icon: XCircle })
        } else if (ret.case_type === 'doa' || ret.case_type === 'transport') {
          actions.push({ label: 'Reemplazar', action: 'replacement', bgColor: 'bg-orange-500 hover:bg-orange-600 text-white', icon: ArrowLeftRight })
        } else {
          actions.push({ label: 'Reembolsar', action: 'refund', bgColor: 'bg-green-500 hover:bg-green-600 text-white', icon: Banknote })
          actions.push({ label: 'Reemplazar', action: 'replacement', bgColor: 'bg-orange-500 hover:bg-orange-600 text-white', icon: ArrowLeftRight })
          actions.push({ label: 'Rechazar', action: 'reject_refund', bgColor: 'bg-red-500 hover:bg-red-600 text-white', icon: BanIcon })
        }
        break
      case 'warranty_rejected':
        // Garantía no procedente: el cierre (reenvío pagado, recogida en tienda,
        // renuncia) se gestiona desde el detalle, pestaña Info → "Garantía no procedente"
        actions.push({ label: 'Gestionar', action: 'detail', bgColor: 'bg-amber-500 hover:bg-amber-600 text-white', icon: Package })
        break
    }
    return actions
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount)
  }

  const getReturnRateBadge = (ret: Return) => {
    const key = getReturnRateKey(ret)
    if (!key) return <span className="text-xs text-slate-300">-</span>
    const info = returnRates[key]
    if (!info) {
      if (loadingRates) return <div className="w-3 h-3 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin mx-auto" />
      return <span className="text-xs text-slate-300">-</span>
    }
    if (info.total_invoices === 0) return <span className="text-xs text-slate-300" title="Sin facturas">-</span>

    const rate = info.rate
    let colorClass: string
    if (rate === 0) colorClass = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    else if (rate < 10) colorClass = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    else if (rate < 20) colorClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    else if (rate < 30) colorClass = 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    else colorClass = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${colorClass}`}
        title={`${info.total_returns} rectificativas / ${info.total_invoices} facturas (ultimos 24 meses)`}
      >
        {rate.toFixed(1)}%
      </span>
    )
  }

  // Pagination helpers
  const totalPages = Math.ceil(total / limit)
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) pages.push(i)
    } else {
      pages.push(0)
      if (page > 2) pages.push('ellipsis')
      for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) pages.push(i)
      if (page < totalPages - 3) pages.push('ellipsis')
      pages.push(totalPages - 1)
    }
    return pages
  }

  const hasActiveFilters = !!(search || customerTypeFilter || dateFrom || dateTo || refundMethodFilter)

  const clearAllFilters = () => {
    setSearch('')
    setCustomerTypeFilter('')
    setRefundMethodFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(0)
  }

  return (
    <div className="p-6">
      {/* Filters */}
      <div className="mb-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por codigo, pedido, cliente..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0) }}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm
                       focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Customer type */}
          <select
            value={customerTypeFilter}
            onChange={(e) => { setCustomerTypeFilter(e.target.value); setPage(0) }}
            className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                     bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
          >
            <option value="">Tipo cliente</option>
            <option value="PVP">PVP</option>
            <option value="PVD">PVD</option>
            <option value="VIP">VIP</option>
          </select>

          {/* Dates */}
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0) }}
              className="px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
              title="Desde"
            />
            <span className="text-slate-400 text-xs">-</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0) }}
              className="px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
              title="Hasta"
            />
          </div>

          {/* Refresh */}
          <button
            onClick={loadReturns}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700
                     bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            title="Refrescar"
          >
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Total + clear */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400 tabular-nums">
              {total} result.
            </span>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-red-600
                         bg-slate-100 dark:bg-slate-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
              >
                <X className="w-3 h-3" />
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Refund method chips */}
        {showRefundMethodChips && (
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <button
              onClick={() => { setRefundMethodFilter(''); setPage(0) }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                !refundMethodFilter
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 ring-1 ring-indigo-200 dark:ring-indigo-800'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
              }`}
            >
              Todos
            </button>
            {Object.entries(REFUND_METHOD_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon
              const isActive = refundMethodFilter === key
              return (
                <button
                  key={key}
                  onClick={() => { setRefundMethodFilter(isActive ? '' : key); setPage(0) }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    isActive
                      ? `${cfg.color} ring-1 ring-current/20`
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {cfg.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Devolucion</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</th>
                {!isIncidents && <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider" title="Tasa de devoluciones (rectificativas/facturas) ultimos 24 meses">% Dev.</th>}
                {caseType === 'transport' && <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Tipo</th>}
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Estado</th>
                {!isIncidents && <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Reembolso</th>}
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Importe</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              ) : returns.length === 0 ? (
                <tr>
                  <td colSpan={isIncidents ? (caseType === 'transport' ? 6 : 5) : 7} className="py-16 text-center">
                    <Package className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">No hay devoluciones</p>
                  </td>
                </tr>
              ) : (
                returns.map((ret) => {
                  const statusConfig = STATUS_CONFIG[ret.status] || STATUS_CONFIG.requested
                  const StatusIcon = statusConfig.icon
                  const actions = getAvailableActions(ret)
                  const hours = hoursElapsed(ret.created_at)
                  const isUrgent = ret.status === 'requested' && hours > 72
                  const isWarning = ret.status === 'requested' && hours > 24 && hours <= 72
                  const isCompleted = ret.status === 'refunded' || ret.status === 'replacement' || ret.status === 'rejected' || ret.status === 'cancelled' || ret.status === 'closed' || ret.status === 'warranty_returned' || ret.status === 'refund_rejected'

                  const rowDimClass = isCompleted ? 'opacity-50' : ''

                  return (
                    <tr
                      key={ret.id}
                      className={`group hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors ${
                        isUrgent ? 'bg-red-50/40 dark:bg-red-900/10' :
                        isWarning ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''
                      }`}
                    >
                      {/* Devolucion: Codigo + Pedido + Fecha */}
                      <td className={`py-3 ${isCompleted ? 'pl-0' : isUrgent ? 'pl-0' : isWarning ? 'pl-0' : 'px-4'}`}>
                        <div className={`flex gap-3 ${isUrgent || isWarning || isCompleted ? 'pl-1' : ''}`}>
                          {/* Left color indicator bar */}
                          {(isUrgent || isWarning || isCompleted) && (
                            <div className={`w-1 self-stretch rounded-full shrink-0 ${
                              isUrgent ? 'bg-red-500' :
                              isWarning ? 'bg-amber-400' :
                              'bg-emerald-500'
                            }`} />
                          )}
                          <div className={rowDimClass}>
                            <button
                              onClick={() => openActionModal(ret, 'detail')}
                              className="font-mono text-sm font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                              {ret.code}
                            </button>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                #{ret.order_reference}
                              </span>
                              <span className="text-slate-300 dark:text-slate-600">·</span>
                              <span className="text-xs text-slate-400 dark:text-slate-500" title={formatDate(ret.created_at)}>
                                {timeAgo(ret.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Cliente: Nombre + Tipo */}
                      <td className={`px-4 py-3 ${rowDimClass}`}>
                        <div className="flex items-center gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-slate-900 dark:text-white truncate max-w-[180px]">
                                {ret.customer_name}
                              </span>
                              <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded ${CUSTOMER_TYPE_COLORS[ret.customer_type] || CUSTOMER_TYPE_COLORS.PVP}`}>
                                {ret.customer_type}
                              </span>
                            </div>
                            <p className="text-xs text-slate-400 dark:text-slate-500 truncate max-w-[220px]">
                              {ret.customer_email}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Tasa devoluciones */}
                      {!isIncidents && (
                      <td className={`px-4 py-3 text-center ${rowDimClass}`}>
                        {getReturnRateBadge(ret)}
                      </td>
                      )}

                      {/* Tipo incidencia (solo transport) */}
                      {caseType === 'transport' && (
                      <td className={`px-4 py-3 ${rowDimClass}`}>
                        {ret.incident_subtype ? (
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                            ret.incident_subtype === 'loss' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                            ret.incident_subtype === 'damage' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                            'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {ret.incident_subtype === 'loss' ? 'Perdida' : ret.incident_subtype === 'damage' ? 'Rotura' : (ret.incident_subtype_other || 'Otra')}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      )}

                      {/* Estado */}
                      <td className={`px-4 py-3 ${rowDimClass}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                          {ret.is_canje && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                              Canje
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Metodo reembolso */}
                      {!isIncidents && (
                      <td className={`px-4 py-3 ${rowDimClass}`}>
                        {ret.refund_method && REFUND_METHOD_CONFIG[ret.refund_method] ? (() => {
                          const cfg = REFUND_METHOD_CONFIG[ret.refund_method!]
                          const Icon = cfg.icon
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md ${cfg.color}`} title={cfg.label}>
                              <Icon className="w-3 h-3" />
                              {cfg.shortLabel}
                            </span>
                          )
                        })() : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">-</span>
                        )}
                      </td>
                      )}

                      {/* Importe */}
                      <td className={`px-4 py-3 text-right ${rowDimClass}`}>
                        <span className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">
                          {formatMoney(ret.refund_amount)}
                        </span>
                        {ret.shipping_cost > 0 && (
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">
                            -{formatMoney(ret.shipping_cost)} envio
                          </p>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className={`px-4 py-3 ${rowDimClass}`}>
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openActionModal(ret, 'detail')}
                            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                            title="Ver detalle"
                          >
                            <Eye className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
                          </button>
                          {actions.map((action, idx) => {
                            const ActionIcon = action.icon
                            return (
                              <button
                                key={idx}
                                onClick={() => openActionModal(ret, action.action)}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full transition-colors ${action.bgColor}`}
                              >
                                <ActionIcon className="w-3 h-3" />
                                {action.label}
                              </button>
                            )
                          })}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400 tabular-nums">
            {page * limit + 1}-{Math.min((page + 1) * limit, total)} de {total}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-30 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              title="Primera"
            >
              <ChevronsLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-30 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              title="Anterior"
            >
              <ChevronLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button>

            {getPageNumbers().map((p, idx) =>
              p === 'ellipsis' ? (
                <span key={`e-${idx}`} className="px-1 text-slate-400">...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`min-w-[32px] h-8 text-sm font-medium rounded-lg transition-colors ${
                    page === p
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {p + 1}
                </button>
              )
            )}

            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-30 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              title="Siguiente"
            >
              <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-30 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              title="Ultima"
            >
              <ChevronsRight className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
        </div>
      )}

      {/* Modales */}
      {selectedReturn && (
        <>
          <ReturnDetailModal
            isOpen={detailModalOpen}
            onClose={() => setDetailModalOpen(false)}
            returnId={selectedReturn.id}
            onStatusChanged={handleActionComplete}
            onOpenAction={(action) => {
              setDetailModalOpen(false)
              openActionModal(selectedReturn, action)
            }}
          />
          <ApproveReturnModal
            isOpen={approveModalOpen}
            onClose={() => setApproveModalOpen(false)}
            returnData={selectedReturn}
            onApproved={handleActionComplete}
          />
          <RejectReturnModal
            isOpen={rejectModalOpen}
            onClose={() => setRejectModalOpen(false)}
            returnData={selectedReturn}
            onRejected={handleActionComplete}
          />
          <ReceiveReturnModal
            isOpen={receiveModalOpen}
            onClose={() => setReceiveModalOpen(false)}
            returnData={selectedReturn}
            onReceived={handleActionComplete}
          />
          <TestReturnModal
            isOpen={testModalOpen}
            onClose={() => setTestModalOpen(false)}
            returnData={selectedReturn}
            onTested={handleActionComplete}
          />
          <RefundReturnModal
            isOpen={refundModalOpen}
            onClose={() => setRefundModalOpen(false)}
            returnData={selectedReturn}
            onRefunded={handleActionComplete}
          />
          <ReplacementReturnModal
            isOpen={replacementModalOpen}
            onClose={() => setReplacementModalOpen(false)}
            returnData={selectedReturn}
            onCompleted={handleActionComplete}
          />
          <RejectRefundModal
            isOpen={rejectRefundModalOpen}
            onClose={() => setRejectRefundModalOpen(false)}
            returnData={selectedReturn}
            onRejected={handleActionComplete}
          />
          <WarrantyResultModal
            isOpen={warrantyResultModalOpen}
            returnId={selectedReturn.id}
            returnData={selectedReturn}
            defaultApproved={warrantyDefaultApproved}
            onClose={() => setWarrantyResultModalOpen(false)}
            onSuccess={handleActionComplete}
          />
        </>
      )}
    </div>
  )
}
